// Thin fetch wrapper around the local API.  Sends X-Demo-User from the persona cookie
// so persona switching works without a full sign-in roundtrip.

import { recordActivityEvent } from './activityCapture.js';

const BASE = import.meta.env?.VITE_API_BASE || '/api';
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const ACTIVITY_BATCH_PATH = '/activity-events/batch';

function readPersonaUserCookie() {
  const match =
    typeof document !== 'undefined' && document.cookie.match(/(?:^|;\s*)dha_nexus_user=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

// Demo/dev CAC identity: mirror the persona cookie so a local session can present
// an EDIPN to the site-access allowlist gate (which, in CAC dev mode, resolves the
// caller from the X-Demo-Edipn header). Set `dha_nexus_edipn=<edipn>` to act as that
// CAC holder — the root-admin EDIPN unlocks the Site Access Control console as owner.
// Ignored by the real cert-based gate when CAC is enabled (production), and only sent
// when the cookie is present, so it is inert unless explicitly opted into.
function readSiteAccessEdipnCookie() {
  const match =
    typeof document !== 'undefined' && document.cookie.match(/(?:^|;\s*)dha_nexus_edipn=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

// URLSearchParams stringifies undefined/null values into the literal strings
// "undefined"/"null", which the API would treat as real filter input.
function cleanQuery(query) {
  if (!query) return null;
  const entries = Object.entries(query).filter(
    ([, value]) => value !== undefined && value !== null,
  );
  return entries.length ? new URLSearchParams(entries) : null;
}

// `signal` is additive (typed-intake commit 4): a caller that can supersede its own
// request — a long AI generation the reporter may stop waiting for — passes an
// AbortSignal so the in-flight fetch is genuinely cancelled rather than left running
// with its result discarded. Omitting it keeps the previous behaviour exactly.
async function request(method, path, { body, query, headers = {}, signal } = {}) {
  const cleaned = cleanQuery(query);
  const params = cleaned ? `?${cleaned.toString()}` : '';
  const url = `${BASE}${path}${params}`;
  const persona = readPersonaUserCookie();
  const siteAccessEdipn = readSiteAccessEdipnCookie();
  const init = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(persona ? { 'X-Demo-User': persona } : {}),
      ...(siteAccessEdipn ? { 'X-Demo-Edipn': siteAccessEdipn } : {}),
      ...headers,
    },
    credentials: 'include',
    ...(signal ? { signal } : {}),
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await fetch(url, init);
  const requestId = res.headers.get('x-request-id');
  const ct = res.headers.get('content-type') || '';
  const payload = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const err = new Error(payload?.error?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = payload?.error?.code;
    err.details = payload?.error?.details;
    err.requestId = payload?.error?.requestId || requestId || null;
    if (MUTATING_METHODS.has(method) && path !== ACTIVITY_BATCH_PATH) {
      recordActivityEvent({
        eventType: 'api',
        eventName: 'api.mutation.error',
        source: 'api-client',
        requestId,
        targetType: 'request',
        targetId: path,
        targetLabel: `${method} ${path}`,
        payload: {
          method,
          path,
          status: res.status,
          errorCode: payload?.error?.code || null,
        },
      });
    }
    throw err;
  }
  if (MUTATING_METHODS.has(method) && path !== ACTIVITY_BATCH_PATH) {
    recordActivityEvent({
      eventType: 'api',
      eventName: 'api.mutation.success',
      source: 'api-client',
      requestId,
      targetType: 'request',
      targetId: path,
      targetLabel: `${method} ${path}`,
      payload: {
        method,
        path,
        status: res.status,
      },
    });
  }
  return payload;
}

export const api = {
  get: (path, opts) => request('GET', path, opts),
  post: (path, body, opts) => request('POST', path, { ...opts, body }),
  put: (path, body, opts) => request('PUT', path, { ...opts, body }),
  patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
  delete: (path, opts) => request('DELETE', path, opts),
};
